/* screenshot.ts — viewport capture for feedback tickets. */

import html2canvas from 'html2canvas';
import { JSON_HEADERS } from './constants';

/** Hard ceiling on the encoded data URL. */
export const MAX_SCREENSHOT_DATA_URL_BYTES = 200_000;
const MAX_CAPTURE_WIDTH = 1280;
const JPEG_QUALITIES = [0.7, 0.6, 0.5, 0.4];
const DOWNSCALES = [0.8, 0.62, 0.45];

function hide(el: Element): void {
  const style = (el as HTMLElement).style;
  if (!style) return;
  style.setProperty('display', 'none', 'important');
  style.setProperty('visibility', 'hidden', 'important');
}

/** Prepare `root` (a CLONE of the live DOM — never the live document) for rasterising. */
export function prepareCloneForCapture(root: Element): number {
  if (!root.ownerDocument) return 0;
  let hidden = 0;
  root.querySelectorAll('.fbtk-root').forEach((el) => {
    hide(el);
    hidden += 1;
  });
  return hidden;
}

function neutraliseUnparseableColors(root: Element): void {
  const view = root.ownerDocument?.defaultView;
  if (!view?.getComputedStyle) return;
  const props = [
    'box-shadow',
    'background',
    'background-color',
    'border-color',
    'color',
    'outline-color',
  ];
  root.querySelectorAll('*').forEach((el) => {
    const style = (el as HTMLElement).style;
    if (!style) return;
    const computed = view.getComputedStyle(el);
    for (const prop of props) {
      if (computed.getPropertyValue(prop)?.includes('color(')) {
        style.setProperty(prop, 'transparent', 'important');
      }
    }
  });
}

export interface CaptureResult {
  dataUrl: string;
  width: number;
  height: number;
}

export interface MarkerPoint {
  x: number;
  y: number;
}

function drawMarker(canvas: HTMLCanvasElement, marker: MarkerPoint): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const sx = canvas.width / Math.max(window.innerWidth, 1);
  const sy = canvas.height / Math.max(window.innerHeight, 1);
  const cx = marker.x * sx;
  const cy = marker.y * sy;
  const r = Math.max(14, Math.round(16 * Math.min(sx, sy)));
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(201, 74, 62, 0.28)';
  ctx.fill();
  ctx.lineWidth = Math.max(2, Math.round(r / 7));
  ctx.strokeStyle = 'rgba(201, 74, 62, 0.9)';
  ctx.stroke();
}

function resize(source: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement {
  const next = document.createElement('canvas');
  next.width = Math.max(1, Math.round(width));
  next.height = Math.max(1, Math.round(height));
  const ctx = next.getContext('2d');
  if (ctx) ctx.drawImage(source, 0, 0, next.width, next.height);
  return next;
}

/** Encode under MAX_SCREENSHOT_DATA_URL_BYTES, or return null. */
export function encodeBounded(canvas: HTMLCanvasElement): CaptureResult | null {
  let source = canvas;
  if (source.width > MAX_CAPTURE_WIDTH) {
    source = resize(source, MAX_CAPTURE_WIDTH, (source.height * MAX_CAPTURE_WIDTH) / source.width);
  }
  for (const quality of JPEG_QUALITIES) {
    const dataUrl = source.toDataURL('image/jpeg', quality);
    if (dataUrl.length <= MAX_SCREENSHOT_DATA_URL_BYTES) {
      return { dataUrl, width: source.width, height: source.height };
    }
  }
  for (const scale of DOWNSCALES) {
    source = resize(source, source.width * scale, source.height * scale);
    const dataUrl = source.toDataURL('image/jpeg', 0.5);
    if (dataUrl.length <= MAX_SCREENSHOT_DATA_URL_BYTES) {
      return { dataUrl, width: source.width, height: source.height };
    }
  }
  return null;
}

/** Capture the current viewport exactly as the reporter sees it. */
export async function captureViewport(
  options: { marker?: MarkerPoint } = {},
): Promise<CaptureResult | null> {
  if (typeof document === 'undefined' || typeof window === 'undefined') return null;
  try {
    const scrollEl = document.querySelector('.v-canvas') as HTMLElement | null;
    const canvas = await html2canvas(document.documentElement, {
      scale: Math.min(window.devicePixelRatio || 1, 2),
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: window.innerWidth,
      height: window.innerHeight,
      x: window.scrollX,
      y: window.scrollY,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      ignoreElements: (el: Element) =>
        Boolean((el as HTMLElement).classList?.contains('fbtk-root')),
      onclone: (_doc: Document, clone: HTMLElement) => {
        prepareCloneForCapture(clone);
        try {
          if (scrollEl) {
            const clonedScrollEl = clone.querySelector('.v-canvas') as HTMLElement | null;
            if (clonedScrollEl) clonedScrollEl.scrollTop = scrollEl.scrollTop;
          }
        } catch {
          /* Fidelity only */
        }
        try {
          neutraliseUnparseableColors(clone);
        } catch {
          /* Cosmetic only */
        }
      },
    });
    if (!canvas || canvas.width < 8 || canvas.height < 8) return null;
    if (options.marker) drawMarker(canvas, options.marker);
    return encodeBounded(canvas);
  } catch {
    return null;
  }
}

export interface UploadInput extends CaptureResult {
  ticketId: string;
}

/** POST the capture; resolve to its id, or null on any failure. */
export async function uploadScreenshot(
  url: string,
  input: UploadInput,
  headers: Record<string, string> = JSON_HEADERS,
): Promise<string | null> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      // JSON_HEADERS carries X-Zoe-Csrf alongside Content-Type.
      headers,
      body: JSON.stringify({
        ticket_id: input.ticketId,
        image_data_url: input.dataUrl,
        width: input.width,
        height: input.height,
      }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { id?: unknown };
    return typeof body?.id === 'string' ? body.id : null;
  } catch {
    return null;
  }
}

/** Pins record coordinates on the scrolling surface (`.v-canvas` when present, else the window). */
export function surfacePointToClientPoint(x: number, y: number): MarkerPoint {
  const surface = document.querySelector('.v-canvas') as HTMLElement | null;
  if (surface) {
    const rect = surface.getBoundingClientRect();
    return { x: rect.left + x - surface.scrollLeft, y: rect.top + y - surface.scrollTop };
  }
  return { x: x - window.scrollX, y: y - window.scrollY };
}
